import { useEffect, useState, useRef } from "react";
import PhotoLogin from "../assets/PhotoLogin.png";
import logoBlue from "../assets/logoBlue.svg";
import { LockClosedIcon, EyeIcon } from "@heroicons/react/24/solid";
import { IoEyeOffOutline } from "react-icons/io5";
import { useCookies } from "react-cookie";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useNavigate, useLocation } from "react-router-dom";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import { jwtDecode } from "jwt-decode";
import { signIn } from "server/Authentication/Requests";
import { SignInOutput } from "server/Authentication/OutputsTypes";
import { BASE_URL } from "server";
import useRole from "hooks/useRole";

const LoginPage = () => {
  // const [cookies, setCookie] = useCookies(["accessTokenV2"]);

  const [identifiant, setIdentifiant] = useState("");

  const [affichage, setAffichage] = useState("");

  const [affichageColor, setAffichageColor] = useState("");

  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [connecting, setConnecting] = useState(false);

  //react-auth-kit doc : https://authkit.arkadip.dev/signin/
  const signInPersist = useSignIn();
  const isAuthenticated = useIsAuthenticated();
  const role = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const handleIdentifiantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifiant(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleToggle = () => {
    setShowPassword(!showPassword);
  };

  const [showPopup, setShowPopup] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  const handleOutsideClick = (e: any) => {
    if (e.target.classList.contains("bg-gray-900")) {
      setShowPopup(false);
    }
  };

  useEffect(() => {
    if (showPopup) {
      document.addEventListener("click", handleOutsideClick);
      // Focus automatique sur l'erreur pour l'accessibilité
      setTimeout(() => {
        errorRef.current?.focus();
      }, 100);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [showPopup]);

  // Surveiller les changements d'authentification et de rôle
  useEffect(() => {
    if (isAuthenticated() && role && connecting === false) {
      console.log(
        "useEffect: User authenticated with role, navigating to:",
        location.state?.from || "/dashboard",
      );
      navigate(location.state?.from || "/dashboard");
    }
  }, [role, connecting]);

  const handleClick = async () => {
    try {
      setConnecting(true);
      const response = await signIn({
        identifiant: identifiant,
        password: password,
      });

      if (response.status != 200) {
        const errorResponse = response.body as { message: string };
        const errorMessage = errorResponse.message || "Erreur inconnue"; // Default message for unknown errors
        setConnecting(false);
        throw new Error(errorMessage);
      }

      const result = response.body as SignInOutput;
      if (result && result.accessToken) {
        //react-auth-kit doc : https://authkit.arkadip.dev/getting_started/signin/
        const decoded = jwtDecode(result.accessToken);
        const signRes = signInPersist({
          auth: {
            token: result.accessToken,
            type: "Bearer",
          },
          refresh: result.refreshToken,
          userState: result.profile,
        });
        if (signRes) {
          setConnecting(false);
          // La redirection sera gérée par le useEffect ci-dessus
        }
      }
    } catch (error: any) {
      console.error(error); // Log the error for debugging purposes

      // Display the error message to the user
      setAffichage(error.message || "Une erreur est survenue.");
      setAffichageColor("red");
      setConnecting(false);
      setShowPopup(true);
    }
  };

  return (
    <div className="w-screen h-screen min-w-screen min-h-screen flex flex-col md:flex-row bg-white overflow-hidden">
      {/* Image à gauche sur desktop/tablette, cachée sur mobile */}
      <div className="hidden md:flex md:w-1/2 h-full items-center justify-center bg-blue-800">
        <img
          src={PhotoLogin}
          alt="Illustration connexion"
          className="w-full h-full object-cover"
        />
      </div>
      {/* Formulaire à droite (ou en haut sur mobile) */}
      <div className="flex flex-1 w-full h-full items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md mx-auto flex flex-col items-center">
          <div className="flex flex-col items-center mb-8">
            <img
              src={logoBlue}
              alt="Logo Bleu de OR"
              className="w-14 h-14 mb-2"
            />
            <div className="text-2xl font-bold text-blue-800 font-['Open Sans']">
              Objectif <span className="text-e3b505">Révision</span>
            </div>
            <div className="text-lg font-semibold text-gray-700 mt-2">
              Se connecter
            </div>
          </div>
          <form
            className="w-full space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              handleClick();
            }}
          >
            {showPopup && (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                className="w-full flex items-center justify-between gap-2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-sm animate-fade-in mb-4 focus:outline-none focus:ring-2 focus:ring-red-400"
                style={{ animation: "fadeInSlideDown 0.3s" }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-red-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01M21 12A9 9 0 11 3 12a9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm font-medium">{affichage}</span>
                </div>
                <button
                  type="button"
                  aria-label="Fermer l'alerte"
                  className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
                  onClick={() => setShowPopup(false)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="identifiant"
              >
                Identifiant
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-800 font-bold">
                  #
                </span>
                <input
                  id="identifiant"
                  className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-300 focus:border-e3b505 focus:ring-2 focus:ring-e3b505 text-gray-700 text-base font-normal outline-none transition-all"
                  type="text"
                  value={identifiant}
                  onChange={handleIdentifiantChange}
                  placeholder="Entrez votre identifiant"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="password"
              >
                Mot de passe
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                  <LockClosedIcon
                    className="w-5 h-5 text-blue-800"
                    aria-hidden="true"
                  />
                </span>
                <input
                  id="password"
                  className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 focus:border-e3b505 focus:ring-2 focus:ring-e3b505 text-gray-700 text-base font-normal outline-none transition-all"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Entrez votre mot de passe"
                  autoComplete="current-password"
                  required
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={handleToggle}
                >
                  {showPassword ? (
                    <IoEyeOffOutline
                      className="w-5 h-5 text-gray-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <EyeIcon
                      className="w-5 h-5 text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-3 rounded-lg bg-blue-800 hover:bg-e3b505 transition-colors text-white text-lg font-bold shadow-md flex items-center justify-center"
              disabled={connecting}
            >
              {connecting ? (
                <svg
                  aria-hidden="true"
                  className="inline w-6 h-6 mr-2 text-white animate-spin fill-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                <span>Se connecter</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
